/**
 * SSO Settings Page
 *
 * Configures the tenant's identity-provider setting. Only Microsoft Entra ID
 * is honest to offer here: it is the platform's real authentication path
 * (existing MSAL app registration). Okta / Google Workspace / custom SAML /
 * custom OIDC are NOT implemented anywhere in the API — there is no SAML
 * processor and no per-tenant OIDC discovery — so those options are shown
 * disabled with an explanation rather than silently accepted and ignored.
 *
 * MFA enforcement correctly belongs to the identity provider's Conditional
 * Access policies at token issuance, not a per-tenant switch here — the
 * control is read-only guidance, not an operable toggle that does nothing.
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Title1,
  Title2,
  Title3,
  Text,
  Card,
  Button,
  Badge,
  makeStyles,
  tokens,
  Field,
  Divider,
  Spinner,
  MessageBar,
  MessageBarBody,
  Dropdown,
  Option,
} from '@fluentui/react-components';
import {
  ShieldLockRegular,
  KeyRegular,
  SaveRegular,
  ArrowResetRegular,
  InfoRegular,
} from '@fluentui/react-icons';
import { settingsApi, type TenantSettingsData } from '../services/api';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXL,
    padding: tokens.spacingVerticalL,
    maxWidth: '900px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  headerIcon: {
    fontSize: '32px',
    color: tokens.colorBrandForeground1,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  sectionIcon: {
    fontSize: '24px',
    color: tokens.colorBrandForeground1,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalL,
  },
  successMessage: {
    marginBottom: tokens.spacingVerticalM,
  },
  infoBox: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    alignItems: 'flex-start',
  },
  infoIcon: {
    color: tokens.colorBrandForeground1,
    marginTop: '2px',
  },
  statusBadge: {
    marginLeft: tokens.spacingHorizontalS,
  },
});

/** The only two states this platform can actually back today. */
const AVAILABLE_SSO_VALUES = new Set(['', 'azure_ad']);

const SSO_PROVIDERS = [
  { value: '', label: 'Not Configured', available: true },
  { value: 'azure_ad', label: 'Microsoft Entra ID (Azure AD)', available: true },
  { value: 'okta', label: 'Okta — not available', available: false },
  { value: 'google', label: 'Google Workspace — not available', available: false },
  { value: 'saml', label: 'Custom SAML 2.0 — not available', available: false },
  { value: 'oidc', label: 'Custom OpenID Connect — not available', available: false },
];

export const SSOSettingsPage: React.FC = () => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState<string | null>(null);

  // Fetch settings from API
  const { data: settingsResponse, isLoading } = useQuery({
    queryKey: ['tenant', 'settings'],
    queryFn: () => settingsApi.getTenantSettings(),
  });

  // Local state for form editing
  const [ssoProvider, setSsoProvider] = useState('');

  // Initialize form state from API data
  useEffect(() => {
    if (settingsResponse?.settings) {
      setSsoProvider(settingsResponse.settings.ssoProvider || '');
    }
  }, [settingsResponse]);

  // Check if SSO configuration is allowed based on subscription
  const canConfigureSso = settingsResponse?.capabilities.canConfigureSso ?? false;

  // Mutation for saving settings
  const saveMutation = useMutation({
    mutationFn: (settings: Partial<TenantSettingsData>) => settingsApi.updateTenantSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant'] });
      setShowSuccess(true);
      setShowError(null);
      setTimeout(() => setShowSuccess(false), 3000);
    },
    onError: (error: Error) => {
      setShowError(error.message);
      setTimeout(() => setShowError(null), 5000);
    },
  });

  const handleSave = () => {
    saveMutation.mutate({ ssoProvider: ssoProvider || undefined });
  };

  const handleReset = () => {
    if (settingsResponse?.settings) {
      setSsoProvider(settingsResponse.settings.ssoProvider || '');
    }
  };

  const isConfiguredAndFunctional = ssoProvider === 'azure_ad';
  const isConfiguredButUnsupported = ssoProvider !== '' && !AVAILABLE_SSO_VALUES.has(ssoProvider);

  if (isLoading) {
    return (
      <div className={styles.page}>
        <Spinner label="Loading SSO settings..." />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Page Header */}
      <div className={styles.header}>
        <ShieldLockRegular className={styles.headerIcon} />
        <div>
          <Title1>
            Single Sign-On (SSO)
            {isConfiguredAndFunctional && (
              <Badge appearance="filled" color="success" className={styles.statusBadge}>
                Configured
              </Badge>
            )}
          </Title1>
          <Text>Configure enterprise authentication for your organization</Text>
        </div>
      </div>

      {/* Messages */}
      {showSuccess && (
        <MessageBar intent="success" className={styles.successMessage}>
          <MessageBarBody>SSO settings saved successfully!</MessageBarBody>
        </MessageBar>
      )}
      {showError && (
        <MessageBar intent="error" className={styles.successMessage}>
          <MessageBarBody>Error saving settings: {showError}</MessageBarBody>
        </MessageBar>
      )}

      {/* Subscription Notice */}
      {!canConfigureSso && (
        <MessageBar intent="warning">
          <MessageBarBody>
            SSO configuration requires a Pro or Enterprise subscription.
            Upgrade your plan to enable enterprise authentication.
          </MessageBarBody>
        </MessageBar>
      )}

      {/* SSO Provider Configuration */}
      <Card>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <KeyRegular className={styles.sectionIcon} />
            <Title2>Identity Provider</Title2>
          </div>

          <div className={styles.form}>
            <Field
              label="SSO Provider"
              hint="Only Microsoft Entra ID is supported today — it is the identity provider that already authenticates every user in this platform."
            >
              <Dropdown
                value={SSO_PROVIDERS.find((p) => p.value === ssoProvider)?.label ?? 'Not Configured'}
                selectedOptions={[ssoProvider]}
                onOptionSelect={(_, data) => {
                  if (data.optionValue !== undefined) setSsoProvider(data.optionValue);
                }}
                disabled={!canConfigureSso}
              >
                {SSO_PROVIDERS.map((provider) => (
                  <Option key={provider.value} value={provider.value} disabled={!provider.available}>
                    {provider.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>

            {isConfiguredAndFunctional && (
              <>
                <Divider />
                <div className={styles.infoBox}>
                  <InfoRegular className={styles.infoIcon} />
                  <div>
                    <Text weight="semibold">Microsoft Entra ID</Text>
                    <Text block size={200}>
                      Your organization already signs in through Microsoft Entra ID. Federation is
                      managed at the Entra app registration level — no additional setup is required
                      on this page.
                    </Text>
                  </div>
                </div>
              </>
            )}

            {isConfiguredButUnsupported && (
              <MessageBar intent="warning">
                <MessageBarBody>
                  This identity provider is not currently supported by the platform. Sign-in
                  continues through Microsoft Entra ID regardless of this setting.
                </MessageBarBody>
              </MessageBar>
            )}
          </div>
        </div>
      </Card>

      {/* Security Requirements — read-only guidance, not an operable switch */}
      <Card>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <ShieldLockRegular className={styles.sectionIcon} />
            <Title2>Security Requirements</Title2>
          </div>

          <div className={styles.form}>
            <Title3>Multi-Factor Authentication</Title3>
            <div className={styles.infoBox}>
              <InfoRegular className={styles.infoIcon} />
              <div>
                <Text weight="semibold">Enforced by your identity provider</Text>
                <Text block size={200}>
                  Multi-factor authentication is enforced by Microsoft Entra ID through Conditional
                  Access policies at sign-in. There is no separate switch to enable it here — manage
                  MFA requirements in your Entra tenant's Conditional Access settings.
                </Text>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Action Buttons */}
      <div className={styles.actions}>
        <Button
          appearance="primary"
          icon={<SaveRegular />}
          onClick={handleSave}
          disabled={saveMutation.isPending || !canConfigureSso}
        >
          {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
        <Button
          appearance="outline"
          icon={<ArrowResetRegular />}
          onClick={handleReset}
          disabled={saveMutation.isPending}
        >
          Reset
        </Button>
      </div>
    </div>
  );
};
